import { SNAPSHOT_INTERVAL_SECONDS } from "./backlogTrend";
import { TenantMailProcessingRow } from "./CoreAPI";
import { historyMeasuredAt, overviewLastReadAt, sumMeasured } from "./derive";

/**
 * The three optional Core measurements, with no instant of their own. Shared
 * by `TrendPoint` (one tenant, one moment) and `AggregatePoint` (all
 * reporting tenants, one bucket) so the two cannot drift apart in shape.
 *
 * Every field is optional in every consumer of this shape. An absent field
 * means the collection could not measure it; a gap must never become a zero,
 * because a zero says "no mail flowed" — a measurement nobody made.
 */
export interface Measurements {
    received?: number;
    sent?: number;
    backlog?: number;
}

/**
 * One measured instant for one tenant.
 *
 * `at` is epoch milliseconds rather than a Date: it compares and sorts
 * cheaply, and it is what a numeric chart axis wants.
 */
export interface TrendPoint extends Measurements {
    at: number;
}

/**
 * One tenant's series, oldest point first.
 *
 * Ordered but not deduplicated: two points may share an instant, or simply
 * fall in the same collection interval, and both survive. A consumer
 * aggregating across tenants must therefore group per tenant rather than
 * pooling points from multiple series flat — pooling would treat two tenants'
 * unrelated near-simultaneous points as if they belonged to one timeline.
 */
export interface TrendSeries {
    tenantKey: string;
    points: TrendPoint[];
}

/**
 * Builds a tenant's series from a history response's rows.
 *
 * Only ever call this with rows from a successful response. A failed request
 * must not reach here — `rows: []` and "no data because the fetch failed" are
 * different facts, and this function has no way to tell them apart once the
 * distinction has been collapsed to `undefined`. Track a failed tenant
 * separately (e.g. the hook's `failedKeys`) and skip calling in for it.
 */
export function seriesFromHistory(tenantKey: string, rows: TenantMailProcessingRow[] | undefined): TrendSeries {
    const points: TrendPoint[] = [];
    let dropped = 0;

    // `rows ?? []` is not the absent-vs-zero defect it resembles: for this
    // collection there is no distinguishable "empty window" versus "no rows
    // key" the way there is for a numeric aggregate. The contract says a 200
    // with no rows means nothing was collected, which is an answer, and both
    // cases produce the same empty series and the same rendering. See spec §9.
    for (const row of rows ?? []) {
        const at = historyMeasuredAt(row);
        if (!at) {
            // A point with no instant cannot be placed on an axis. Dropping it
            // is the only honest option; inventing a timestamp would put a
            // measurement at a time it was not taken. Contract §2.2 says
            // lastAttemptAt is one of the two per-tick facts a history row
            // does carry, so an unparseable one is a server-contract
            // violation, not an expected gap — worth a warning below.
            dropped += 1;
            continue;
        }
        points.push({
            at: at.getTime(),
            received: row.throughput?.received,
            sent: row.throughput?.sent,
            backlog: row.mailboxes?.backlog,
        });
    }

    if (dropped > 0) {
        // One warning per call, not per row, so a bad batch doesn't flood the
        // console. This is spec §9's channel for a contract inconsistency the
        // page can't otherwise surface — a dropped row would otherwise look
        // identical to a tenant that simply reported nothing.
        console.warn(
            `seriesFromHistory: dropped ${dropped} row(s) with no parseable instant for tenant "${tenantKey}"`,
        );
    }

    // The contract says rows arrive oldest-to-newest. Sorting anyway is cheap,
    // and appendPoint's strict-newer rule depends on the last element really
    // being the newest.
    points.sort((a, b) => a.at - b.at);

    return { tenantKey, points };
}

/**
 * Appends one point derived from a landing-view row, or returns the series
 * untouched.
 *
 * Rejection returns the SAME object, so a consumer can rely on referential
 * identity to mean "nothing changed".
 *
 * This is the refresh path: rather than refetching 40 histories every five
 * minutes, each overview poll contributes one point from data already on the
 * wire.
 *
 * Reads `lastAttemptAt` via `overviewLastReadAt` (the landing meaning) rather
 * than `historyMeasuredAt` — spec §6 records why that is correct here.
 */
export function appendPoint(series: TrendSeries, row: TenantMailProcessingRow): TrendSeries {
    // Silent, unlike seriesFromHistory's warning for the same shape of
    // rejection: there, an absent instant is a contract violation. Here, on
    // the landing route, it is a legitimate NeverCollected tenant — warning
    // would fire once per never-collected tenant on every five-minute poll,
    // forever.
    const at = overviewLastReadAt(row);
    if (!at) {
        return series;
    }

    const point: TrendPoint = {
        at: at.getTime(),
        received: row.throughput?.received,
        sent: row.throughput?.sent,
        backlog: row.mailboxes?.backlog,
    };

    // All-absent is nothing to record. A measured zero IS a measurement and
    // must get through — that distinction is the whole feature.
    if (point.received === undefined && point.sent === undefined && point.backlog === undefined) {
        return series;
    }

    // Strictly newer, or not at all. Both routes can report the same snapshot,
    // and a duplicate point with an equal value breaks a strictly-increasing
    // run — silently suppressing a real "behind" finding in Spec 2. This same
    // rule is what makes a stale row's old timestamp harmless: it can only
    // produce an instant the series already has data at or beyond.
    //
    // `.at(-1)` (not `points[points.length - 1]`) so the type system carries
    // the `| undefined`: on an empty series that keeps the guard below
    // visibly necessary rather than looking like dead code to a future
    // tidying pass.
    const newest = series.points.at(-1);
    if (newest !== undefined && point.at <= newest.at) {
        return series;
    }

    // Spreading `series` first means its `tenantKey` always wins over the
    // row's — `TenantMailProcessingRow.tenantKey` is optional and structurally
    // absent from the landing shape this function is called with, so there is
    // nothing to guard against, only a choice to record: the row is never
    // consulted for it.
    return { ...series, points: [...series.points, point] };
}

const BUCKET_MS = SNAPSHOT_INTERVAL_SECONDS * 1000;

/** One bucket of the cross-tenant aggregate. */
export interface AggregatePoint extends Measurements {
    /** Bucket start, epoch milliseconds. */
    at: number;
    /** How many tenants contributed any measurement to this bucket. */
    tenants: number;
}

export interface AggregateSeries {
    points: AggregatePoint[];
    /** Fewest tenants contributing to any bucket. */
    minTenants: number;
    /** Most tenants contributing to any bucket. */
    maxTenants: number;
    /** Tenants asked about, contributing or not. The denominator. */
    total: number;
}

/**
 * Collapses one tenant's points within one bucket into a single contribution.
 *
 * The two measurement kinds are NOT treated alike, and this is the whole
 * reason this function exists separately rather than inline.
 *
 * `received` and `sent` are deltas over the snapshot interval. `CoreAPI.ts`
 * calls them "the only fields that may be summed across time", so two of one
 * tenant's readings in a bucket cover disjoint intervals and **sum**.
 *
 * `backlog` is a gauge. Contract §2.6 forbids summing a gauge across time —
 * adding a tenant's 9 and its later 12 would invent a level of 21 that never
 * existed. So the gauge takes the value from whichever point has the greatest
 * `at` among those that measured it — latest *measured*, not simply the last
 * point's, so a newer point that did not measure backlog cannot discard an
 * older measurement we hold. Does not require `points` in any particular
 * order.
 */
export function collapseTenantBucket(points: TrendPoint[]): Measurements {
    const withBacklog = points.filter(p => p.backlog !== undefined);
    const backlog =
        withBacklog.length === 0
            ? undefined
            : withBacklog.reduce((latest, p) => (p.at >= latest.at ? p : latest)).backlog;

    return {
        received: sumMeasured(points.map(p => p.received)).total,
        sent: sumMeasured(points.map(p => p.sent)).total,
        backlog,
    };
}

/**
 * Sums every tenant's series into one, bucketed to the snapshot interval.
 *
 * Why buckets. Tenants are collected independently, so their snapshot instants
 * differ by seconds and there is no shared x to sum over. Rounding each point
 * down to a five-minute boundary is what makes "sum across tenants at this
 * time" mean anything at all.
 *
 * The population is deliberately NOT fixed: each bucket sums whichever tenants
 * reported in it (spec §2.7). The consequence, recorded here because it is a
 * real cost and not an oversight: the line's denominator changes under it, so
 * a coverage change and a genuine throughput change look identical. A fixed
 * population was recommended and rejected; spec §7 has both sides. The
 * mitigation is `minTenants`/`maxTenants`, which the coverage note renders as
 * a range so a population shift is at least discoverable.
 *
 * Absent stays absent per field, not per bucket: `sumMeasured` returns an
 * undefined total when nothing measured it, so a field no tenant reported
 * renders as a gap rather than a zero.
 */
export function aggregateSeries(seriesList: TrendSeries[], total: number): AggregateSeries {
    // bucket start -> tenantKey -> that tenant's already-collapsed contribution.
    // Nested rather than a flat point list on purpose: TrendPoint carries no
    // tenant identity, so a flat pool cannot count distinct tenants, and it
    // would let one tenant's two readings in a bucket be summed as if they
    // came from two tenants.
    const buckets = new Map<number, Map<string, Measurements>>();

    for (const series of seriesList) {
        const perBucket = new Map<number, TrendPoint[]>();
        for (const point of series.points) {
            const at = Math.floor(point.at / BUCKET_MS) * BUCKET_MS;
            const held = perBucket.get(at);
            if (held) {
                held.push(point);
            } else {
                perBucket.set(at, [point]);
            }
        }

        for (const [at, points] of perBucket) {
            const collapsed = collapseTenantBucket(points);

            // A history row can record an attempt with a valid instant but
            // no measurable figures — the absent-not-zero premise explicitly
            // permits this. Counting it toward `tenants` would overstate
            // coverage: the note would say "summed across N tenants" when
            // one of them added no data. Same all-absent check appendPoint
            // uses; a measured zero must still count.
            if (collapsed.received === undefined && collapsed.sent === undefined && collapsed.backlog === undefined) {
                continue;
            }

            const tenants = buckets.get(at) ?? new Map<string, Measurements>();
            tenants.set(series.tenantKey, collapsed);
            buckets.set(at, tenants);
        }
    }

    const points: AggregatePoint[] = Array.from(buckets.entries())
        .sort((left, right) => left[0] - right[0])
        .map(([at, tenants]) => {
            const contributions = Array.from(tenants.values());
            return {
                at,
                received: sumMeasured(contributions.map(c => c.received)).total,
                sent: sumMeasured(contributions.map(c => c.sent)).total,
                backlog: sumMeasured(contributions.map(c => c.backlog)).total,
                // Distinct tenants, not points. This is what keeps the coverage
                // note from claiming more tenants than exist.
                tenants: tenants.size,
            };
        });

    const counts = points.map(p => p.tenants);

    return {
        points,
        minTenants: counts.length === 0 ? 0 : Math.min(...counts),
        maxTenants: counts.length === 0 ? 0 : Math.max(...counts),
        total,
    };
}

/**
 * Reduces a series to `target` points for drawing, by nearest-neighbour
 * sampling at a fixed stride.
 *
 * This samples rather than aggregates, unlike `collapseTenantBucket`'s
 * careful sum-vs-gauge reduction across tenants. That is deliberate:
 * `downsample` stays generic over `T` so both `TrendPoint` and
 * `AggregatePoint` series can share it, and re-deriving sum/gauge semantics
 * for an already-collapsed `AggregatePoint` would not be meaningful. The
 * cost is that a collection gap becomes non-deterministic in the drawing: a
 * single missed measurement has roughly a 1-in-stride chance of landing on
 * a sampled index and being drawn as a break, and is otherwise stepped over
 * as if collection were continuous — and the reverse happens too, drawing a
 * gap where only the one sampled instant in an otherwise-fine stride was
 * absent. This is a drawing-fidelity trade, not a data operation; nothing
 * downstream should read the drawn series to reason about gaps.
 *
 * Generic over the point type so the aggregate and per-tenant series share
 * it. Full-resolution series stay in memory; only the drawing is reduced.
 *
 * `target` is not one point per pixel: a line chart reads as continuous
 * well below that, so callers should pick a target comfortably under their
 * drawing width in pixels, not equal to it.
 *
 * Preserving the first and last points is not cosmetic: the newest value is
 * the one an operator reads first, and an even stride can land short of it,
 * which would misreport the current state.
 *
 * Takes and returns `readonly` arrays: the no-op branches return the
 * caller's own array, and a mutable return type would let a downstream
 * in-place sort or reverse corrupt state still held elsewhere (React state,
 * in particular) — intermittently, since only the small-series branch
 * aliases.
 */
export function downsample<T>(points: readonly T[], target: number): readonly T[] {
    if (target < 2 || points.length <= target) {
        return points;
    }

    const out: T[] = [];
    const step = (points.length - 1) / (target - 1);
    for (let i = 0; i < target; i++) {
        out.push(points[Math.min(points.length - 1, Math.round(i * step))]);
    }
    return out;
}

/**
 * Drops points older than `cutoff` (epoch ms, inclusive).
 *
 * The initial fetch asks for a 24-hour window, but the append path never
 * re-enforces it: each five-minute poll only ever appends, so a series left
 * running keeps growing past the window it claims to represent.
 *
 * That is worse than a stale label, because `downsample` deliberately
 * preserves `points[0]`. The out-of-window point is therefore precisely the
 * one that can never be dropped from a drawing without this function.
 *
 * REQUIRES `points` in ascending order, which `seriesFromHistory` guarantees
 * and `appendPoint` preserves.
 *
 * Returns the SAME object when nothing is dropped, matching `appendPoint`'s
 * contract so the hook can treat referential identity as "nothing changed" for
 * both operations.
 */
export function trimToWindow(series: TrendSeries, cutoff: number): TrendSeries {
    const keepFrom = series.points.findIndex(point => point.at >= cutoff);

    if (keepFrom === -1) {
        // Every point is older than the cutoff. An empty series is the honest
        // answer: we hold nothing inside the window we claim to show.
        return series.points.length === 0 ? series : { ...series, points: [] };
    }
    if (keepFrom === 0) {
        return series;
    }
    return { ...series, points: series.points.slice(keepFrom) };
}
